import React, { createContext, useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  setAuthToken,
  injectUpdateAccessToken,
  refreshAccessToken,
  logoutUser,
  getUserMe,
} from "../api/quontxApi";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    injectUpdateAccessToken(setAccessToken);
  }, []);

  // On initial load, intelligently check if we should even attempt a refresh
  useEffect(() => {
    const bootstrapAuth = async () => {
      // 1. Check for the logout flag in sessionStorage
      const sessionStatus = sessionStorage.getItem("sessionStatus");

      // 2. If the user has explicitly logged out in this session, skip the refresh attempt.
      if (sessionStatus === "logged_out") {
        setLoading(false);
        return;
      }

      // 3. If no flag, proceed with the refresh attempt as before
      try {
        const response = await refreshAccessToken();
        const { access_token } = response.data;
        setAccessToken(access_token);
        setAuthToken(access_token);
        const decoded = jwtDecode(access_token);
        setUser({ username: decoded.sub });
      } catch (error) {
        console.log("No valid session found on initial load.");
      } finally {
        setLoading(false);
      }
    };
    bootstrapAuth();
  }, []);

  const login = async (username, password) => {
    const params = new URLSearchParams();
    params.append("grant_type", "");
    params.append("username", username);
    params.append("password", password);

    // const config = { headers: { "Content-Type": "application/json" } };
    const response = await api.post("login", { username, password });

    const { access_token } = response.data;
    setAccessToken(access_token);
    setAuthToken(access_token);
    const decoded = jwtDecode(access_token);
    setUser({ username: decoded.sub });

    // IMPORTANT: On a successful login, remove the logout flag
    sessionStorage.removeItem("sessionStatus");

    // Before navigating get user profile and save it to localstorage for further use.
    const userRes = await getUserMe();
    localStorage.setItem("username", userRes.data.username);
    localStorage.setItem("name", userRes.data.name);
    localStorage.setItem("email", userRes.data.email);
    localStorage.setItem("is_superuser", userRes.data.is_superuser);
    localStorage.setItem("applications", userRes.data.applications);

    navigate(userRes.data.is_superuser ? "/extractions" : "/node-analysis");
  };

  const logout = async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error(
        "Server logout failed, clearing client-side session anyway.",
        error
      );
    } finally {
      setAccessToken(null);
      setUser(null);
      setAuthToken(null);

      // IMPORTANT: Set the logout flag before navigating away
      sessionStorage.setItem("sessionStatus", "logged_out");

      // Delete the user data from localstorage
      localStorage.removeItem("username");
      localStorage.removeItem("name");
      localStorage.removeItem("email");
      localStorage.removeItem("is_superuser");
      localStorage.removeItem("applications");

      navigate("/login");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-qtx-dark">
        <div className="text-white text-xl">Initializing Session...</div>
      </div>
    );
  }

  const value = { accessToken, user, loading, login, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
